// Fleet self-update request — sent by the mothership to a runner on
// `fleet.update.<runnerId>` (core NATS, one-way push) when a new runner binary is
// ready. The runner fetches the signed binary, verifies the Ed25519 signature,
// drains its in-flight assignment, and swaps the binary before exiting so its
// supervisor (systemd Restart=always) picks up the new version.
//
// Snake_case: crosses NATS verbatim, same convention as other fleet wire types.

import { z } from 'zod';

/**
 * Wire payload the mothership pushes on `fleet.update.<runnerId>` when a new
 * runner binary has been released and signed.
 */
export const SelfUpdateRequestSchema = z.object({
  /** Semver version string, e.g. `"1.4.2"`. Logged for auditing; not parsed. */
  version: z.string().min(1),
  /** HTTPS URL of the signed runner binary (platform-specific). */
  url: z.string().url(),
  /** HTTPS URL of the detached Ed25519 signature file for `url`. */
  sig_url: z.string().url(),
});
export type SelfUpdateRequest = z.infer<typeof SelfUpdateRequestSchema>;

// ── Release manifest (plan 101 doc 08 step 1, #1039) ──────────────────────────
//
// What CI publishes next to the binaries it just built, signed and hashed
// (`scripts/runner-release.ts`). It lives HERE, beside the request it feeds,
// because three parties read the same shape and none of them may restate it: the
// CI publisher writes it, the `runner_releases` row is written from it, and the
// rollout controller turns one artifact into the `SelfUpdateRequest` above.

/** Build targets a runner release ships. One `bun build --compile --target` each. */
export const RUNNER_RELEASE_TARGETS = ['linux-x64', 'linux-arm64'] as const;
export const RunnerReleaseTargetSchema = z.enum(RUNNER_RELEASE_TARGETS);
export type RunnerReleaseTarget = z.infer<typeof RunnerReleaseTargetSchema>;

/** Release channels. `canary` is the slice a staged rollout soaks on first. */
export const RUNNER_RELEASE_CHANNELS = ['stable', 'canary'] as const;
export const RunnerReleaseChannelSchema = z.enum(RUNNER_RELEASE_CHANNELS);
export type RunnerReleaseChannel = z.infer<typeof RunnerReleaseChannelSchema>;

/**
 * The runner canary hold's committed file (#6783), `.github/runner-release-channel.json`:
 * the ONE source of the channel CI publishes a runner build to, with the one-line reason
 * it says what it says. `scripts/runner-release.ts` refuses a file this does not parse.
 */
export const RunnerReleaseChannelFileSchema = z.strictObject({
  channel: RunnerReleaseChannelSchema,
  reason: z.string().trim().min(1),
});

// ── The push/pull timing contract (plan 101 runner lifecycle, slice 06) ────────
//
// The box's pull is SUBORDINATE to the mothership's push, and the thing that makes
// it so is arithmetic over three numbers that live on two sides of the fleet plane:
// the autoroll cadence and the rollout's confirm deadline + soak on the mothership
// (`apps/worker`), and the maturity window a box waits before it may pull a release
// (`apps/runner`). Apps never import apps, so until this block each side restated
// its numbers and a test on the box side scraped the worker's SOURCE TEXT with a
// regex to prove the relationship — a guard that read `15 * 60_000` out of a comment
// as happily as out of the constant. The three defaults are defined ONCE here; each
// side's own default is derived from them and its own test pins that derivation, so
// lengthening the soak on the mothership without raising the box's window is a red
// test in the same PR, never a pull that fires before wave 1 exists.

/** Default `cron.fleet_autoroll` cadence — the minute step of `FLEET_AUTOROLL_CRON`. */
export const FLEET_AUTOROLL_CADENCE_MS = 15 * 60_000;

/** Default confirm deadline: how long a pushed box has to come back on the new build. */
export const FLEET_ROLLOUT_CONFIRM_MS = 10 * 60_000;

/** Default soak: how long a confirmed canary must keep running the build before waves. */
export const FLEET_ROLLOUT_SOAK_MS = 15 * 60_000;

/**
 * Margin the box's maturity window keeps past the push's worst case — ten rollout
 * ticks (30 s each), so a release is never pulled on the very tick its canary could
 * still be halted.
 */
export const FLEET_UPDATE_MATURITY_MARGIN_MS = 5 * 60_000;

/**
 * The floor a box's maturity window must clear for the pull to stay subordinate:
 * publish → the tick that records + rolls it (cadence) → canary confirmed or failed
 * (confirm deadline) → confirmed canary soaked (soak) → waves start. A box that pulls
 * before this instant is the fast path the maturity gate exists to prevent.
 */
export const FLEET_UPDATE_PUSH_WORST_CASE_MS =
  FLEET_AUTOROLL_CADENCE_MS + FLEET_ROLLOUT_CONFIRM_MS + FLEET_ROLLOUT_SOAK_MS;

// ── The published layout (#1777) ──────────────────────────────────────────────
//
// Two DISJOINT prefixes, on purpose. `releases/<version>/` is immutable — those exact
// bytes are what a `SelfUpdateRequest` pins, so a halted canary can never be dragged
// forward by a moving alias. `channels/<channel>/latest/` is the mutable "current build"
// alias humans and `attach-vm.sh` install from. Because the two are separate prefixes, a
// version string that happens to read like a channel name can never alias one.
//
// These lived in `@developerz/storage` `keys.ts` until #1777, beside the R2 client that
// writes them. They moved HERE because the BOX now derives an immutable manifest url
// from the alias url it was pointed at (`apps/runner/src/update-check.ts`), and
// `@developerz/storage` pulls in the AWS SDK + sharp — which `apps/runner` deliberately
// keeps out of its ~94 MB compiled binary (`src/tools/ci-log.ts` mirrors constants by
// hand for exactly that reason). `keys.ts` re-exports them, so no producer moved.

/** The prefix every published runner-release object lives under. */
export const RUNNER_RELEASE_PREFIX = 'runner/';

/**
 * Fold a version into one safe path segment (`0.0.0+sha-abc1234` → `0.0.0-sha-abc1234`).
 *
 * Three moves: collapse anything outside `[A-Za-z0-9._-]` (so a version can never smuggle
 * in a separator), strip leading dots and dashes (so a segment can never be `..` or read
 * as a flag), and refuse a result with no alphanumeric left. That last check is the
 * load-bearing one: without it `'///'` collapses to `'-'` and a garbage version quietly
 * publishes to — or, on the box, READS from — a real key instead of failing loudly.
 */
export function runnerReleaseVersionSegment(version: string): string {
  const segment = version
    .trim()
    .replace(/[^A-Za-z0-9._-]+/g, '-')
    .replace(/^[.-]+/, '');
  if (!/[A-Za-z0-9]/.test(segment)) {
    throw new RangeError('runner release version has no usable characters');
  }
  return segment;
}

/** Immutable per-version prefix: `runner/releases/<version>`. */
export function runnerReleaseVersionPrefix(version: string): string {
  return `${RUNNER_RELEASE_PREFIX}releases/${runnerReleaseVersionSegment(version)}`;
}

/**
 * Immutable PROMOTION prefix (#6783): `runner/releases/<version>/promoted/<channel>`.
 *
 * Where the manifest pair CI signed for `channel` at promote time lives, beside the
 * release's own pair. A promoted release's own manifest names the channel it was
 * PUBLISHED to (canary), so a halt that withdraws an alias back to it copies this pair,
 * never that one: the box follows a manifest's `channel` to that channel's list, and
 * measures maturity from its `published_at`. Written once by `scripts/runner-promote.ts`.
 */
export function runnerReleasePromotionPrefix(
  version: string,
  channel: RunnerReleaseChannel,
): string {
  return `${runnerReleaseVersionPrefix(version)}/promoted/${channel}`;
}

/** Mutable per-channel alias prefix: `runner/channels/<channel>/latest`. */
export function runnerReleaseChannelPrefix(channel: RunnerReleaseChannel): string {
  return `${RUNNER_RELEASE_PREFIX}channels/${channel}/latest`;
}

/**
 * The ONE shape rule for a 32-byte value as lowercase hex (a sha-256 digest or a
 * raw Ed25519 key). Exported because the two ends of the fleet signing keypair must
 * agree on it byte-for-byte and neither may restate it (#1251): this schema, the
 * api's `DZ_UPDATE_PUBKEY` resolver (`apps/api/src/index.ts`), and the runner's
 * box-boot arming gate (`apps/runner/src/update-pubkey.ts`). No `g`/`y` flag — a
 * stateful regex would alternate answers across repeated `.test()` calls.
 */
export const HEX32_PATTERN = /^[0-9a-f]{64}$/;

/** Lowercase hex sha-256 / raw-Ed25519-key digest, exactly 32 bytes. */
const Hex32Schema = z.string().regex(HEX32_PATTERN, 'expected 64 lowercase hex chars');

/** One built binary plus the two files that prove what it is. */
export const RunnerReleaseArtifactSchema = z.object({
  target: RunnerReleaseTargetSchema,
  /** HTTPS URL of the binary. */
  url: z.string().url(),
  /** HTTPS URL of its detached raw Ed25519 signature (64 bytes, not armored). */
  sig_url: z.string().url(),
  /** Lowercase hex sha-256 of the binary — the `DZ_RUNNER_BINARY_SHA256` install pin. */
  sha256: Hex32Schema,
  /** Size of the binary in bytes. */
  bytes: z.number().int().positive(),
});
export type RunnerReleaseArtifact = z.infer<typeof RunnerReleaseArtifactSchema>;

/**
 * The manifest published alongside a runner release, at both the immutable
 * per-version prefix and the channel's mutable `latest` alias.
 *
 * `pubkey` is an operator-facing FINGERPRINT ("which key signed this build"), never
 * an input to verification: a box verifies against the `DZ_UPDATE_PUBKEY` it was
 * enrolled with (`apps/runner/src/self-update.ts`), so a manifest that names a key
 * we never issued simply fails closed on every box instead of being trusted.
 */
export const RunnerReleaseManifestSchema = z.object({
  /** Version string the built binaries report on their presence beat. */
  version: z.string().min(1),
  channel: RunnerReleaseChannelSchema,
  /** Full git sha this was built from. */
  commit: z.string().min(1),
  published_at: z.string().datetime(),
  /** Hex Ed25519 public key the signatures were verified against IN CI, before publish. */
  pubkey: Hex32Schema,
  artifacts: z.array(RunnerReleaseArtifactSchema).min(1),
});
export type RunnerReleaseManifest = z.infer<typeof RunnerReleaseManifestSchema>;

// ── The channel's recent releases (#1777) ─────────────────────────────────────
//
// **The maturity gate measured a moving target.** `gradeManifest` asks "is the newest
// release mature?", and the alias manifest names exactly ONE version — so on an active
// repo, where the head is replaced faster than the gate elapses, the countdown restarts
// against a fresh head forever and the pull check is starved. Measured 2026-08-13: five
// of seven publish intervals were 15 min against a 45-75 min effective floor
// (`DZ_UPDATE_MIN_AGE_MS` 45 min plus a per-box stagger of up to 30 min); a prod box sat
// at `905952ms of 2708477ms` against a head that had already been superseded twice.
//
// This is what lets the box see a release that HAS matured: the channel's recent
// releases, so the box can install the newest one older than ITS OWN window.
//
// **The box picks, and that is the load-bearing choice.** A single publisher-computed
// "settled" pointer cannot work here, by arithmetic: the effective floor is per-box
// (maturity PLUS that box's stagger offset), so one pointer computed against one window
// is refused as `immature` by every box whose floor is longer than it. With the 15-min
// rollout soak it is refused by ALL of them; raise the publisher's window to the 45-min
// maturity default and it still starves every box whose stagger offset exceeds one
// publish cadence. A list costs one array and converges for every box.
//
// **It is a SIBLING OBJECT, not a manifest field, and that is a race, not a taste.**
// Only the mothership can exclude a halted release (`runner_releases.yanked_at` is a
// database fact, and CI holds no database credentials — see `scripts/runner-release-record.ts`),
// so the writer has to be the worker. Folding this into `manifest.json` would make the
// worker read-modify-write an object CI overwrites on every publish, and a lost update
// there re-points the channel alias at an OLDER release for a whole tick. A sibling
// object has ONE writer and cannot race the publisher at all.
//
// Snake_case, like every other published shape here.

/** How many releases a channel's recent list may name. */
export const RUNNER_CHANNEL_RECENT_MAX = 10;

/** One entry: which release, and when it landed. */
export const RunnerChannelReleaseSchema = z.object({
  version: z.string().min(1),
  published_at: z.string().datetime(),
});

/**
 * The channel's installable releases, NEWEST FIRST, published at
 * `runner/channels/<channel>/latest/recent.json`.
 *
 * Every entry is installable as of the write: a halted release is excluded, so #1754's
 * withdrawal reaches this list and not only the alias. It is a snapshot, never a
 * promise — a release halted after the write stays named until the next one, exactly as
 * the alias itself does.
 *
 * `channel` is carried so a box can assert the list it fetched belongs to the channel it
 * was pointed at; a mismatch means an operator pointed `DZ_UPDATE_MANIFEST_URL` at one
 * channel while something served another, and the box refuses rather than installing
 * across channels.
 */
export const RunnerChannelRecentSchema = z.object({
  channel: RunnerReleaseChannelSchema,
  releases: z.array(RunnerChannelReleaseSchema).max(RUNNER_CHANNEL_RECENT_MAX),
});
export type RunnerChannelRecent = z.infer<typeof RunnerChannelRecentSchema>;

/**
 * The `fleet.update.<runnerId>` payload for one target of a release, or `undefined`
 * when the release carries no binary for that target — a box on an architecture this
 * release skipped must be left alone, never handed another architecture's binary.
 */
export function selfUpdateRequestFor(
  manifest: RunnerReleaseManifest,
  target: RunnerReleaseTarget,
): SelfUpdateRequest | undefined {
  const artifact = manifest.artifacts.find((a) => a.target === target);
  if (!artifact) return undefined;
  return { version: manifest.version, url: artifact.url, sig_url: artifact.sig_url };
}

/**
 * The build target for a box advertising `arch` (`BoxSpecs.arch`, i.e. Node/Bun's
 * `os.arch()`), or `undefined` when we cannot say which binary that box runs.
 *
 * The rollout controller pins ONE artifact per box, so this is the join between
 * what a box reports about itself and what CI built. It is deliberately a CLOSED
 * mapping rather than a `linux-${arch}` template: an unmapped, empty, or hostile
 * arch string must answer "unknown" and leave that box alone, never synthesize a
 * target name that would then miss every artifact (or, worse, match one built for
 * a different machine). Every runner release is Linux-only — the fleet is Linux
 * VMs — so the OS half is a constant here, not a second input.
 *
 * Each machine has SEVERAL spellings in the wild, so the table enumerates the
 * aliases instead of narrowing to one: Bun on Linux x86_64 reports `x86_64` while
 * Bun on macOS reports `x64`, and `uname -m` contributes `amd64` / `aarch64`. A
 * table that listed only `x64` / `arm64` silently dropped three of the fleet's
 * four boxes as `unknownTarget` and starved them of every release (#1230). Widening
 * is not opening: the safety property is "no synthesis", not "one spelling", so an
 * unlisted, empty, differently-cased, or padded string still answers undefined.
 */
export function runnerReleaseTargetForArch(
  arch: string | null | undefined,
): RunnerReleaseTarget | undefined {
  switch (arch) {
    case 'x64':
    case 'x86_64':
    case 'amd64':
      return 'linux-x64';
    case 'arm64':
    case 'aarch64':
      return 'linux-arm64';
    default:
      return undefined;
  }
}

// ── Rollout progress (plan 101 runner lifecycle, slice 06) ────────────────────
//
// The durable state of one staged rollout PASS, as the mothership persists it
// (`runner_rollouts.attempts`, `@developerz/db`) and resumes it after a worker
// redeploy (`apps/worker/src/fleet/update-rollout.ts`). Lives here because both
// sides read the same shape and neither may restate it; validated at the READ, so a
// torn row is a named fault at resume rather than a pass grading garbage.

/**
 * What became of one box the pass published to.
 *
 * `superseded` is a third settled verdict, neither failure nor confirm: the push
 * was overtaken by a build the push did not name — the box pull-swapped to a newer
 * release that landed mid-pass, so at its confirm deadline the sweep's copy shows a
 * version that is neither the pushed target nor the one the box left. It must never
 * move a halt tally: the attempt is evidence about a build nobody pushed, not about
 * the target this pass is grading.
 */
export const RolloutAttemptVerdictSchema = z.enum([
  'open',
  'confirmed',
  'failed',
  'refused',
  'superseded',
]);
export type RolloutAttemptVerdict = z.infer<typeof RolloutAttemptVerdictSchema>;

/** One box the pass published to, with what the pass concluded about it so far. */
export const RolloutProgressAttemptSchema = z.object({
  runnerId: z.string().min(1),
  accountId: z.string().min(1),
  fromVersion: z.string().min(1).nullable(),
  toVersion: z.string().min(1),
  /** ISO-8601 — when the push was written to the outbox. The confirm deadline runs from here. */
  publishedAt: z.string().datetime(),
  wave: z.number().int().nonnegative(),
  verdict: RolloutAttemptVerdictSchema,
  /** ISO-8601 — when the box was seen on the build. The soak runs from here. `confirmed` only. */
  confirmedAt: z.string().datetime().optional(),
  /**
   * `failed`: the failure reason; `refused`: the box's own refusal reason id;
   * `superseded`: the version the box took instead of the pushed target.
   */
  reason: z.string().min(1).optional(),
  /**
   * ISO-8601 — when the pass re-pushed this attempt to a live box that had said nothing by
   * half its confirm deadline (#6422). Once per attempt; absent on every row written before.
   */
  repushedAt: z.string().datetime().optional(),
});
export type RolloutProgressAttempt = z.infer<typeof RolloutProgressAttemptSchema>;

/** Whether the canary has confirmed and soaked (`waves`) or not yet (`canary`). */
export const RolloutPhaseSchema = z.enum(['canary', 'waves']);
export type RolloutPhase = z.infer<typeof RolloutPhaseSchema>;

/** Everything a restarted pass needs to continue where the last one stopped. */
export const RolloutProgressStateSchema = z.object({
  /** The release the pass is driving — moves forward on a re-target. */
  version: z.string().min(1),
  phase: RolloutPhaseSchema,
  /** Rounds the pass has run; a resumed pass continues the count toward its ceiling. */
  rounds: z.number().int().nonnegative(),
  attempts: z.array(RolloutProgressAttemptSchema),
});
export type RolloutProgressState = z.infer<typeof RolloutProgressStateSchema>;

/** Terminal and live statuses of a `runner_rollouts` row. `in_progress` is the only live one. */
export const RUNNER_ROLLOUT_STATUSES = [
  'in_progress',
  'complete',
  'halted',
  'incomplete',
  'yanked',
  'no_release',
] as const;
export const RunnerRolloutStatusSchema = z.enum(RUNNER_ROLLOUT_STATUSES);
export type RunnerRolloutStatus = z.infer<typeof RunnerRolloutStatusSchema>;
